"use client";

import { useAnimationSequence } from "@ui/hooks/useAnimationSequence";
import { useEffect } from "react";
import { useTranslations } from "next-intl";
import { GridItem, MODEL_NAMES, TIMING, TRANSITIONS } from "../types";
import {
  PromptContentArea,
  ContentPlaceholder,
  ContentProcessingOverlay,
  PromptControlBar,
  ControlItem,
  IconVideo,
  IconQuality,
  IconAspectRatio,
  SendButton,
  ContentVideo,
  ModelSelector,
  IconLayers,
} from "../components";

const UPSCALE_STATES = [
  "IDLE",
  "OPEN_MODEL_SELECTOR",
  "SELECT_MODEL",
  "CLOSE_MODEL_SELECTOR",
  "UPLOAD_VIDEO",
  "SENDING",
  "PROCESSING",
  "RESULT",
  "FINISHED",
  "RESET",
] as const;

export function UpscaleScene({
  onGenerationStarted,
  onComplete,
  autoStart = true,
}: {
  onGenerationStarted: (mediaUrl: string, mediaType: "image" | "video", label: string) => void;
  onComplete: (result: GridItem) => void;
  autoStart?: boolean;
}) {
  const t = useTranslations("ClientKeys.VideoImageGenerationHero");
  const { motion: m, start, store } = useAnimationSequence(UPSCALE_STATES);

  // Auto-start animation if enabled
  useEffect(() => {
    if (!autoStart) return;

    const timer = setTimeout(() => {
      start();
    }, 500);
    return () => clearTimeout(timer);
  }, [start, autoStart]);

  useEffect(() => {
    const unsubscribe = store.subscribe(
      state => state.state,
      currentState => {
        const currentStateName = UPSCALE_STATES[currentState];

        if (currentStateName === "PROCESSING") {
          onGenerationStarted(
            "https://eleven-public-cdn.elevenlabs.io/payloadcms/g8le0zphho-upscale-video-original.mp4",
            "video",
            t("processing_upscaling"),
          );
        }

        if (currentStateName === "FINISHED") {
          onComplete({
            type: "upscale",
            media: "https://eleven-public-cdn.elevenlabs.io/payloadcms/ln9976jy68f-upscale-video-result.mp4",
            status: "complete",
            hasAudio: true,
          });
        }
      },
    );
    return unsubscribe;
  }, [store, onGenerationStarted, onComplete, t]);

  return (
    <>
      <m.div className='tw-flex tw-gap-2'>
        <PromptContentArea
          sequencedMotion={m}
          animate={{
            UPLOAD_VIDEO: { opacity: 1 },
          }}
          transition={{
            UPLOAD_VIDEO: { ...TRANSITIONS.fast, delay: TIMING.DELAY.SHORT },
          }}
        >
          <ContentPlaceholder sequencedMotion={m} initial={{ opacity: 1 }} />

          <ContentVideo
            sequencedMotion={m}
            src='https://eleven-public-cdn.elevenlabs.io/payloadcms/g8le0zphho-upscale-video-original.mp4'
            render={["UPLOAD_VIDEO", "-RESET"]}
            animatePresence={{ initial: false }}
            initial={{ opacity: 0 }}
            animate={{
              UPLOAD_VIDEO: { opacity: 1 },
              FINISHED: { opacity: 0 },
            }}
            exit={{ opacity: 0, transition: TRANSITIONS.slow }}
            transition={{
              UPLOAD_VIDEO: { ...TRANSITIONS.slow, delay: 0.2 },
              FINISHED: TRANSITIONS.instant,
            }}
          />

          {/* Processing overlay */}
          <ContentProcessingOverlay
            sequencedMotion={m}
            render={["PROCESSING"]}
            initial={{ opacity: 0 }}
            animate={{
              PROCESSING: { opacity: 1 },
              RESULT: { opacity: 0 },
            }}
            transition={{
              PROCESSING: TRANSITIONS.medium,
              RESULT: TRANSITIONS.medium,
            }}
            className='tw-fixed tw-inset-0 tw-z-10'
            spinner={
              <m.div
                animate={{
                  PROCESSING: { rotate: 360 },
                }}
                transition={{
                  PROCESSING: TRANSITIONS.spinner,
                }}
                className='tw-w-4 tw-h-4 tw-border-2 tw-border-white tw-border-t-transparent tw-rounded-full'
              />
            }
          />
        </PromptContentArea>
        <PromptContentArea sequencedMotion={m} render={["IDLE", "-CLOSE_MODEL_SELECTOR"]}>
          <ContentPlaceholder sequencedMotion={m} initial={{ opacity: 1 }} />
        </PromptContentArea>
      </m.div>
      <PromptControlBar
        sequencedMotion={m}
        controls={
          <>
            <ControlItem
              icon={<IconVideo className='tw-size-5' />}
              sequencedMotion={m}
              label={
                <div className='tw-relative tw-h-full tw-flex tw-items-center'>
                  <m.span className='tw-type-sm' render={["IDLE", "-CLOSE_MODEL_SELECTOR"]}>
                    {MODEL_NAMES.LIPSYNC}
                  </m.span>
                  <m.span className='tw-type-sm' render={["CLOSE_MODEL_SELECTOR"]}>
                    {MODEL_NAMES.UPSCALE}
                  </m.span>

                  <ModelSelector
                    sequencedMotion={m}
                    currentModel={MODEL_NAMES.LIPSYNC}
                    targetModel={MODEL_NAMES.UPSCALE}
                    render={["OPEN_MODEL_SELECTOR", "SELECT_MODEL", "-CLOSE_MODEL_SELECTOR"]}
                    animatePresence={{ initial: false }}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{
                      OPEN_MODEL_SELECTOR: { opacity: 1, y: 0 },
                      SELECT_MODEL: { opacity: 1, y: 0 },
                    }}
                    exit={{ opacity: 0, y: -10, transition: TRANSITIONS.fast }}
                    transition={{
                      OPEN_MODEL_SELECTOR: { ...TRANSITIONS.medium, delay: TIMING.DELAY.SHORT },
                      SELECT_MODEL: TRANSITIONS.slow,
                    }}
                  />
                </div>
              }
            />

            <ControlItem
              icon={<IconLayers className='tw-size-5' />}
              label='1'
              sequencedMotion={m}
              render={["IDLE", "-CLOSE_MODEL_SELECTOR"]}
            />

            <ControlItem
              icon={<IconQuality className='tw-size-5' />}
              label='4K'
              className='tw-hidden md:tw-flex'
              sequencedMotion={m}
              render={["CLOSE_MODEL_SELECTOR"]}
            />

            <ControlItem
              icon={<IconAspectRatio className='tw-size-5' />}
              label='16:9'
              sequencedMotion={m}
              render={["CLOSE_MODEL_SELECTOR"]}
              className='tw-hidden md:tw-flex'
            />
          </>
        }
        button={
          <SendButton
            sequencedMotion={m}
            animate={{
              SENDING: {
                scale: [1, 0.92, 1],
                backgroundColor: ["#F5F3F1", "#dbdad9", "#F5F3F1"], // Darker when pressed
              },
            }}
            transition={{
              SENDING: TRANSITIONS.slow,
            }}
          />
        }
      />
    </>
  );
}
